import React from 'react';

import { DatasetListItem, UserRoleForDataset } from 'types/Dataset';
import Card from 'components/card/Card';
import DatasetCardBody from 'views/Datasets/components/DatasetCardBody';
import { NButton, NIcon } from '@innersource-nn/novoui-react';
import { useRecentlyViewed } from './recentlyViewed/RecentlyViewedProvider';
import { useModal } from 'hooks/useModal';
import { ShareObjectStatus } from 'types/Share';

const HomeCard = ({
    label,
    statistics,
    description,
    created,
    updated,
    datasetUri,
    shares,
    userRoleForDataset,
}: DatasetListItem) => {
    const { openDatasetAccess } = useModal();
    const { addToRecentlyViewed } = useRecentlyViewed();

    const getStatusOfDataset = () => {
        const userRoles = [
            UserRoleForDataset.BusinessOwner,
            UserRoleForDataset.DataSteward,
            UserRoleForDataset.Creator,
            UserRoleForDataset.Admin,
        ];

        if (userRoles.includes(userRoleForDataset)) {
            return {
                status: 'Approved',
                color: 'info',
                icon: (
                    <NIcon
                        iconName="person"
                        size={16}
                        className="fill-true-blue"
                    />
                ),
                tooltip: `You are the ${userRoleForDataset} of this dataset`,
            };
        }

        const statusConfigurations = new Map([
            [
                ShareObjectStatus.Approved,
                {
                    status: 'Approved',
                    color: 'success',
                    icon: (
                        <NIcon
                            iconName="lockOpen"
                            size={16}
                            className="fill-true-blue"
                        />
                    ),
                    tooltip: 'Access granted',
                },
            ],
            [
                ShareObjectStatus.PendingApproval,
                {
                    status: 'PendingApproval',
                    color: 'warning',
                    icon: (
                        <NIcon
                            iconName="clockUpdate"
                            size={16}
                            className="fill-true-blue"
                        />
                    ),
                    tooltip: 'Your share request is waiting for approval',
                },
            ],
            [
                ShareObjectStatus.Draft,
                {
                    status: 'Draft',
                    color: 'warning',
                    icon: (
                        <NIcon
                            iconName="save"
                            size={16}
                            className="fill-true-blue"
                        />
                    ),
                    tooltip:
                        'You have an existing share request draft saved for this dataset',
                },
            ],
        ]);

        const matchingStatus = shares?.nodes
            .map((node) => node.status)
            .find((status) => statusConfigurations.has(status));

        return matchingStatus
            ? statusConfigurations.get(matchingStatus)
            : undefined;
    };

    const status = shares && getStatusOfDataset();
    const onClickRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
        // Avoid to be redirected to the dataset
        event.preventDefault();
        openDatasetAccess({
            resourceUri: datasetUri,
            datasetName: label,
        });
    };
    const actions = (
        <NButton
            onClick={onClickRequest}
            variant="outline"
            square
            size="small"
            className="hidden group-hover:block top-0 right-0 transition ease-in-out delay-600"
        >
            <div className="flex justify-center gap-8">
                <NIcon iconName="key" size={16} className="fill-true-blue" />
                Request Access
            </div>
        </NButton>
    );

    return (
        <Card
            onClick={() => addToRecentlyViewed(datasetUri)}
            label={label}
            uri={`/dataset/${datasetUri}/`}
            type="dataset"
            body={<DatasetCardBody statistics={statistics} status={status} />}
            description={description}
            updated={updated || created}
            actions={actions}
        />
    );
};

export default HomeCard;
